import type { ComputedRef } from 'vue'
import type { CustomerDetails } from '@entities/customer'

import { useCustomerStore } from '@entities/customer'
import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'
import { amplitudeIcon } from '@shared/ui/icons'

import zohoIcon from '../ui/images/zoho.svg'

interface CustomerIntegrationLink {
  name: string
  url: string
  id: string
  /** An image mark instead of the default external-link icon. */
  image: string | null
  imageWidth: number | null
}

interface CustomerInfoReturn {
  refDetails: ComputedRef<CustomerDetails | null>
  refLinks: ComputedRef<CustomerIntegrationLink[]>
  refChangelogUrl: ComputedRef<string>
}

const toId = (value: string | number | null): string => (value === null || value === '' ? '' : String(value))

/** Links and Other blocks of the General info tab — both read-only views of `customers.details`. */
export const useCustomerInfo = (): CustomerInfoReturn => {
  const customerStore = useCustomerStore()

  const refDetails = computed<CustomerDetails | null>(() => customerStore.getDetails)

  const refLinks = computed<CustomerIntegrationLink[]>(() => {
    const details = refDetails.value
    if (!details) return []

    const links: CustomerIntegrationLink[] = [
      { name: 'Landing', url: details.links.landing_url ?? '', id: '', image: null, imageWidth: null },
      { name: 'Payment methods', url: details.links.payment_methods_url ?? '', id: '', image: null, imageWidth: null },
      { name: 'Amplitude', url: details.links.amplitude_url ?? '', id: toId(details.device_id), image: amplitudeIcon, imageWidth: 18 },
      { name: 'Zoho', url: details.links.crm_url ?? '', id: toId(details.crm_id), image: zohoIcon, imageWidth: 42 },
      { name: 'Respond', url: details.links.respond_url ?? '', id: toId(details.respond_id), image: null, imageWidth: null },
    ]

    if (details.links.stripe_url) {
      links.push({ name: 'Stripe', url: details.links.stripe_url, id: toId(details.stripe_id), image: null, imageWidth: null })
    }

    return links
  })

  const refChangelogUrl = computed<string>(() =>
    resolveUrl(AppRoute.CustomersChangelog, { id: refDetails.value?.id }) ?? '')

  return {
    refDetails,
    refLinks,
    refChangelogUrl,
  }
}
